import React, { useCallback, useEffect, useState } from 'react';
import API from '../services/API';
import {
  AnonymousIdentityDto,
  AnonymousIdentitySession,
  IdentityDto,
  IdentityWithCodeRequest,
} from '@quartermaster/qmlib-api/dist/types/Signup';
import { Auth } from '@quartermaster/qmlib-api/dist/types/Me';
import { useSelector } from 'react-redux';
import { selectAuthJWT } from '../redux/auth/selectors';

interface Props {
  onSuccess: (props: Auth) => void;
  onNeedsSignup: (props: AnonymousIdentityDto) => void;
  onError?: (err: any) => void;
  onDisconnect?: () => void;
  onIdentityCheck?: (props: IdentityDto) => void;
  url: string;
  redirectUri: string;
  isConnected?: boolean;
  signOnText?: string;
  serviceName: string;
  renderButton: (
    props: {
      href: string | undefined;
      onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
      isConnected: boolean;
      signOnText: string;
    },
  ) => React.ReactElement;
}

const SSOContainer: React.FC<Props> = ({
  onSuccess,
  onError,
  onNeedsSignup,
  onDisconnect,
  onIdentityCheck,
  url,
  isConnected = false,
  redirectUri,
  signOnText = '',
  serviceName,
  renderButton,
}) => {
  const [isConnectedToServer, setIsConnectedToServer] = useState(false);
  const jwt = useSelector(selectAuthJWT);

  useEffect(() => {
    if (jwt) {
      API.SignupService()
      .getIdentity(({ data }) => {
        const hasIdentity = data.identities.find(
          id => id.provider === serviceName.toLowerCase(),
        );

        if (hasIdentity) {
          setIsConnectedToServer(true);
          onIdentityCheck && onIdentityCheck(hasIdentity);
        }
      })
      .catch(err => {
        console.error(err);
      });
    }
  }, []);

  useEffect(
    () => {
      const queryParams = new URLSearchParams(window.location.search);

      if (queryParams.has('code')) {
        const authCode = queryParams.get('code')!;

        API.SignupService()
          .createIdentitySession<IdentityWithCodeRequest>(
            {
              code: authCode,
              redirect_url: redirectUri,
              provider: serviceName.toLowerCase(),
            },
            ({ data }) => {
              onSuccess(data);
            },
          )
          .catch(err => {
            if (err.status === 404) {
              const typedError = err.data.identity as AnonymousIdentitySession;

              if (typedError.access_token) {
                onNeedsSignup(typedError);
              } else {
                onError && onError(err);
              }
            } else {
              throw err;
            }
          });
      }
    },
    [window.location.search],
  );

  const handleDisconnect = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (isConnectedToServer) {
        API.SignupService()
          .deleteIdentity(serviceName.toLowerCase())
          .then(() => {
            onDisconnect && onDisconnect();
          })
          .catch(err => {
            onError && onError(err);
          });
      } else {
        onDisconnect && onDisconnect();
      }
    },
    [],
  );

  return renderButton({
    href: !isConnected ? url : undefined,
    onClick: isConnected ? handleDisconnect : undefined,
    isConnected,
    signOnText,
  });
};

export default SSOContainer;
